import React, { useState } from 'react';
import { Card, Badge, Button, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  ClipboardIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DotsVerticalIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/solid';
import moment from 'moment';
import { toast } from 'react-toastify';

const SharedAccessLinkCard = ({
  link,
  onEdit,
  onDelete,
  onDeactivate,
  onViewLogs,
  onCopyUrl,
}) => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);

  const currentLanguage = i18n.language;
  const dateFormat = currentLanguage === 'pl' ? 'DD.MM.YYYY HH:mm' : 'MMM D, YYYY HH:mm';

  const getStatusBadge = () => {
    if (!link.is_valid) {
      return (
        <Badge bg='danger' className='ms-2'>
          {t('shared-access.inactive')}
        </Badge>
      );
    }
    if (link.is_expired) {
      return (
        <Badge bg='warning' className='ms-2'>
          {t('shared-access.expired')}
        </Badge>
      );
    }
    return (
      <Badge bg='success' className='ms-2'>
        {t('shared-access.active')}
      </Badge>
    );
  };

  const handleCopyUrl = async () => {
    if (link.share_url) {
      try {
        await navigator.clipboard.writeText(link.share_url);
        setCopied(true);
        onCopyUrl?.();
        toast.success(t('shared-access.url-copied'));
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error(t('shared-access.copy-error') || 'Failed to copy URL');
      }
    }
  };

  const activeScopes = Object.entries(link.allowed_scopes || {})
    .filter(([key, value]) => value && key !== 'health_reports') // Exclude health_reports - temporarily disabled
    .map(([key]) => key);

  return (
    <Card border='0' className='shadow-sm mb-3'>
      <Card.Body>
        <div className='d-flex justify-content-between align-items-start mb-3'>
          <div className='flex-grow-1'>
            <div className='d-flex align-items-center mb-2'>
              <h6 className='mb-0'>{link.description || t('shared-access.no-description')}</h6>
              {getStatusBadge()}
            </div>
            <p className='text-muted small mb-0'>{link.token}</p>
          </div>
          <Dropdown>
            <Dropdown.Toggle
              variant='link'
              className='text-dark p-0'
              style={{ border: 'none', boxShadow: 'none' }}
            >
              <DotsVerticalIcon className='icon icon-xs' />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={handleCopyUrl}>
                <ClipboardIcon className='icon icon-xs me-2' />
                {copied ? t('shared-access.copied') : t('shared-access.copy-url')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onViewLogs?.(link)}>
                <EyeIcon className='icon icon-xs me-2' />
                {t('shared-access.view-logs')}
              </Dropdown.Item>
              {link.is_valid && (
                <Dropdown.Item onClick={() => onEdit?.(link)}>
                  <PencilIcon className='icon icon-xs me-2' />
                  {t('shared-access.edit-link')}
                </Dropdown.Item>
              )}
              {link.is_active && link.is_valid && (
                <Dropdown.Item onClick={() => onDeactivate?.(link)}>
                  <XCircleIcon className='icon icon-xs me-2' />
                  {t('shared-access.deactivate-link')}
                </Dropdown.Item>
              )}
              <Dropdown.Divider />
              <Dropdown.Item onClick={() => onDelete?.(link)} className='text-danger'>
                <TrashIcon className='icon icon-xs me-2' />
                {t('shared-access.delete-link')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className='row g-3 mb-3'>
          <div className='col-md-6'>
            <div className='d-flex align-items-center text-muted small'>
              <ClockIcon className='icon icon-xs me-2' />
              <span>
                {t('shared-access.created')}: {moment(link.created).format(dateFormat)}
              </span>
            </div>
          </div>
          <div className='col-md-6'>
            <div className='d-flex align-items-center text-muted small'>
              <ClockIcon className='icon icon-xs me-2' />
              <span>
                {t('shared-access.expires-at')}: {moment(link.expires_at).format(dateFormat)}
              </span>
            </div>
          </div>
        </div>

        <div className='row g-3 mb-3'>
          <div className='col-md-6'>
            <div className='text-muted small'>
              <strong>{t('shared-access.access-count')}:</strong> {link.access_count || 0}
            </div>
          </div>
          {link.last_accessed_at && (
            <div className='col-md-6'>
              <div className='text-muted small'>
                <strong>{t('shared-access.last-accessed')}:</strong>{' '}
                {moment(link.last_accessed_at).format(dateFormat)}
              </div>
            </div>
          )}
        </div>

        {activeScopes.length > 0 && (
          <div>
            <strong className='small'>{t('shared-access.allowed-scopes')}:</strong>
            <div className='mt-1'>
              {activeScopes.map((scope) => (
                <Badge key={scope} bg='secondary' className='me-1'>
                  {t(`shared-access.scopes.${scope}`)}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default SharedAccessLinkCard;

